import { useCallback, useRef, useState } from 'react'
import { raiseOnly, type Occupancy, type SheetHeight } from './occupancy'
import type { AgentPart, AgentPartState } from './parts/types'

/**
 * The one part currently on screen.
 *
 * ADDRESSED, NOT STACKED. A new part REPLACES the old one rather than
 * pushing onto a pile, so there is no scrollback growing on this layer and
 * no way to end up three questions deep with no route out. It is a
 * conversation; the transcript lives on the Support page.
 *
 * It clears itself on an answer, because a part that lingers after being
 * answered is a part the next turn has to tidy up, and the agent will
 * forget.
 */
export function useAgentParts(): {
  current: AgentPartState | null
  show: (part: AgentPart, mode?: Occupancy, height?: SheetHeight) => void
  clear: () => void
  /** Raise the mobile sheet. Never lowers it: see `raiseOnly`. */
  raise: (height: SheetHeight) => void
} {
  const [current, setCurrent] = useState<AgentPartState | null>(null)
  const seq = useRef(0)

  const show = useCallback(
    (part: AgentPart, mode: Occupancy = 'panel', height: SheetHeight = 'half') => {
      seq.current += 1
      setCurrent({ part, mode, height, seq: seq.current })
    },
    [],
  )

  const clear = useCallback(() => {
    setCurrent(null)
  }, [])

  const raise = useCallback((height: SheetHeight) => {
    setCurrent((now) =>
      now === null ? now : { ...now, height: raiseOnly(now.height, height) },
    )
  }, [])

  return { current, show, clear, raise }
}
